import { CountryCode, getCountries, getCountryCallingCode, isValidPhoneNumber } from 'libphonenumber-js';
import { parsePhoneNumber } from 'libphonenumber-js/max';

import { PrefixModel, OTHER_PREFIX } from '../models/phone-number.model';

export class PhoneNumberHelper {
  static readonly EUROPEAN_COUNTRIES: CountryCode[] = [
    'AT',
    'BE',
    'BG',
    'HR',
    'CY',
    'CZ',
    'DK',
    'EE',
    'FI',
    'FR',
    'DE',
    'GR',
    'HU',
    'IE',
    'IT',
    'LV',
    'LT',
    'LU',
    'MT',
    'NL',
    'PL',
    'PT',
    'RO',
    'SK',
    'SI',
    'ES',
    'SE',
    'GB',
    'AL',
    'AD',
    'AM',
    'BY',
    'BA',
    'CH',
    'GE',
    'IS',
    'LI',
    'MC',
    'MD',
    'ME',
    'MK',
    'NO',
    'RU',
    'RS',
    'TR',
    'UA',
  ];

  // Détecte si c'est le préfixe "Autre" (numéros spéciaux)
  static isOtherPrefix(prefix: PrefixModel): boolean {
    return prefix?.code === 'OTHER';
  }

  static getCountryName(countryCode: string): string {
    return new Intl.DisplayNames(['fr'], { type: 'region' }).of(countryCode) || countryCode;
  }

  static loadEUCountries(): PrefixModel[] {
    const countries: PrefixModel[] = getCountries()
      .filter(code => this.EUROPEAN_COUNTRIES.includes(code as CountryCode))
      .map(code => {
        const name = this.getCountryName(code);
        const dialCode = getCountryCallingCode(code as CountryCode);
        return {
          code: code as CountryCode,
          name,
          dialCode,
          displayLabel: `${name} (+${dialCode})`,
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));

    // Ajouter le préfixe "Autre" en fin de liste
    countries.push(OTHER_PREFIX);

    return countries;
  }

  static getPrefix(prefixes: PrefixModel[], countryCode: string = 'FR'): PrefixModel | undefined {
    return prefixes.find(c => c.code === countryCode) || prefixes.find(c => c.code === 'FR');
  }

  static sanitizePhoneNumber(phone: string): string {
    return phone.replace(/\D/g, '');
  }

  static formatPhoneNumber(dialCode: string, sanitizedPhone: string): string {
    return `+${dialCode}${sanitizedPhone}`;
  }

  static isValidPhoneFormat(phoneNumber: string, prefix: PrefixModel): boolean {
    // Mode "Autre" : pas de validation (saisie libre)
    if (this.isOtherPrefix(prefix)) {
      return true;
    }

    // Validation standard pour les pays normaux
    if (!prefix || !prefix.code) {
      return false;
    }

    const sanitizedPhone = this.sanitizePhoneNumber(phoneNumber);
    const fullNumber = this.formatPhoneNumber(prefix.dialCode, sanitizedPhone);

    return isValidPhoneNumber(fullNumber, prefix.code as CountryCode);
  }
}
